import { attachmentKey, type Attachment, type AttachmentTarget } from "@gjwtrack/domain";
import { Paperclip, Trash2, Upload, X } from "lucide-react";
import { useEffect, useRef, useState, type DragEvent } from "react";
import { client, useClient } from "../lib/client.ts";
import { absTime, relTimeAgo, userName } from "../lib/format.ts";
import { VideoPlayer } from "./VideoPlayer.tsx";

/** YouTrack's attachment lightbox: dark backdrop, close at the top right, the video playing at once. Esc / backdrop closes. */
function VideoPreview({ a, onClose }: { a: Attachment; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-8" onClick={onClose} role="dialog" aria-label={a.name} data-test="attachment-preview">
      <button className="absolute top-3 right-3 flex h-8 w-8 items-center justify-center rounded border border-white/40 text-white/80 hover:border-white hover:text-white" title="Close (Esc)" onClick={onClose}>
        <X size={16} />
      </button>
      <div className="flex max-h-full max-w-full flex-col items-center gap-2" onClick={(e) => e.stopPropagation()}>
        {/* Opened by a click, so autoplay with sound is allowed. Video.js v10 player (lazy chunk). */}
        <VideoPlayer src={client.http.attachmentUrl(a.id)} mime={a.mime} name={a.name} autoPlay className="max-h-[85vh] w-[min(90vw,1280px)]" />
        <div className="text-[13px] text-white/70">{a.name}</div>
      </div>
    </div>
  );
}

const fmtSize = (n: number): string => (n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);

/** Attachment list with drop-zone upload, image thumbnails, download and delete. */
export function Attachments({ target, canAdd }: { target: AttachmentTarget; canAdd: boolean }) {
  const key = attachmentKey(target);
  const state = useClient((s) => s.attachments[key]);
  const schema = useClient((s) => s.schema);
  const me = useClient((s) => s.me);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(0);
  const [preview, setPreview] = useState<Attachment | null>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void client.loadAttachments(target);
    return () => client.forgetAttachments(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const upload = async (files: FileList | File[]): Promise<void> => {
    for (const f of Array.from(files)) {
      setBusy((b) => b + 1);
      try {
        await client.uploadAttachment(target, f, f.name);
      } catch {
        /* toast shown */
      } finally {
        setBusy((b) => b - 1);
      }
    }
  };
  const onDrop = (e: DragEvent): void => {
    e.preventDefault();
    setOver(false);
    if (canAdd && e.dataTransfer.files.length) void upload(e.dataTransfer.files);
  };

  const items = state?.items ?? [];
  if (!canAdd && items.length === 0) return null;
  return (
    <section
      className={`mb-8 ml-10 rounded-md ${over ? "bg-accent-soft ring-2 ring-accent/50" : ""}`}
      onDragOver={(e) => {
        if (!canAdd) return;
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
    >
      <div className="mb-2 flex items-center gap-2 text-[13px] text-muted">
        <Paperclip size={14} />
        Attachments {items.length > 0 && <span className="tabular-nums">{items.length}</span>}
        {canAdd && (
          <>
            <button className="ml-2 text-value hover:underline" onClick={() => input.current?.click()}>
              <Upload size={13} className="mr-1 inline" />
              {busy ? `uploading ${busy}…` : "Upload"}
            </button>
            <input ref={input} type="file" multiple hidden onChange={(e) => e.target.files && void upload(e.target.files).then(() => (e.target.value = ""))} />
          </>
        )}
      </div>
      {items.length === 0 ? (
        canAdd && <div className="rounded border border-dashed border-line px-3 py-3 text-faint">Drop files here or click Upload.</div>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {items.map((a) => {
            const url = client.http.attachmentUrl(a.id);
            const mine = a.authorId === me?.id || me?.isAdmin;
            const video = a.mime.startsWith("video/");
            return (
              <li key={a.id} className="group relative w-40 rounded border border-line bg-surface p-2" title={`${a.name}\n${fmtSize(a.size)} · ${userName(schema, a.authorId ?? 0)} · ${absTime(a.createdAt)}`}>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener"
                  className="block"
                  onClick={(e) => {
                    // Videos open in the lightbox; plain click only, so middle-click / ⌘-click still open the file itself.
                    if (!video || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                    e.preventDefault();
                    setPreview(a);
                  }}
                >
                  {a.mime.startsWith("image/") ? (
                    <img src={url} alt={a.name} className="mb-1 h-24 w-full rounded object-cover" loading="lazy" />
                  ) : video ? (
                    <video src={url} preload="metadata" muted playsInline className="mb-1 h-24 w-full rounded bg-black object-cover" />
                  ) : (
                    <div className="mb-1 flex h-24 items-center justify-center rounded bg-sunken text-xs text-muted uppercase">{a.name.split(".").pop()?.slice(0, 6)}</div>
                  )}
                  <div className="truncate text-[13px]">{a.name}</div>
                  <div className="text-xs text-muted">
                    {fmtSize(a.size)} · {relTimeAgo(a.createdAt)}
                  </div>
                </a>
                {mine && (
                  <button className="icon-btn absolute top-1 right-1 h-6 w-6 bg-surface opacity-0 group-hover:opacity-100" title="Delete" onClick={() => confirm(`Delete ${a.name}?`) && void client.deleteAttachment(target, a.id)}>
                    <Trash2 size={13} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {preview && <VideoPreview a={preview} onClose={() => setPreview(null)} />}
    </section>
  );
}
